import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Input,
  Select,
  Button,
  Table,
  Avatar,
  Form,
  Typography,
  Space,
  Upload,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Swal from "sweetalert2";
import api from "../../../api/api";
import defaultAvatar from "./assets/avartar.jpg";

const { Title, Text } = Typography;

const emptyForm = {
  id: null,
  username: "",
  email: "",
  password: "",
  confirm_password: "",
  user_type: "admin",
  gender: "",
  profile_image: null,
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [form] = Form.useForm();
  const [editingUser, setEditingUser] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  // ================= FETCH =================
  const fetchUsers = async () => {
    try {
      const res = await api.get("/api/users/users-users/");
      setUsers(res.data);
    } catch {
      Swal.fire("Error", "Failed to load users", "error");
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ================= FILTER =================
  const filteredUsers = users.filter((u) => {
    return (
      (u.username.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())) &&
      (roleFilter ? u.user_type === roleFilter : true)
    );
  });

  // ================= SUBMIT =================
  const handleSubmit = async (values) => {
    const data = new FormData();

    data.append("username", values.username);
    data.append("email", values.email);
    data.append("user_type", values.user_type);
    data.append("gender", values.gender);

    if (values.password) data.append("password", values.password);
    if (values.confirm_password)
      data.append("confirm_password", values.confirm_password);

    if (values.profile_image?.file)
      data.append("profile_image", values.profile_image.file);

    try {
      setLoading(true);

      if (editingUser) {
        await api.put(`/api/users/users-users/${editingUser.id}/`, data);
        Swal.fire("Success", "User updated successfully", "success");
      } else {
        await api.post("/register/", data);
        Swal.fire("Success", "User created successfully", "success");
      }

      form.resetFields();
      setEditingUser(null);
      setPreview(null);
      fetchUsers();
    } catch (err) {
      const msg =
        err.response?.data &&
        Object.values(err.response.data).flat().join("\n");

      Swal.fire("Error", msg || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  // ================= EDIT =================
  const handleEdit = (user) => {
    setEditingUser(user);

    form.setFieldsValue({
      username: user.username,
      email: user.email,
      user_type: user.user_type,
      gender: user.gender,
    });

    setPreview(
      user.profile_image
        ? user.profile_image.startsWith("http")
          ? user.profile_image
            //  production:
          : `https://zuluwhiskeyshipping.online${user.profile_image}`
          // development:
          // : `http://127.0.0.1:8000${user.profile_image}`
        : null
    );
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    Swal.fire({
      title: "Delete User?",
      text: "This cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/api/users/users-users/${id}/`);
          Swal.fire("Deleted", "User removed successfully", "success");
          fetchUsers();
        } catch {
          Swal.fire("Error", "Failed to delete user", "error");
        }
      }
    });
  };

  // ================= TABLE =================
  const columns = [
    {
      title: "Avatar",
      dataIndex: "profile_image",
      render: (img) => (
        <Avatar
          size={45}
          src={
            img
              ? img.startsWith("http")
                ? img
                // : `http://127.0.0.1:8000${img}` 
                : `https://zuluwhiskeyshipping.online${img}` 
              : defaultAvatar
          }
        />
      ),
    },
    { title: "Username", dataIndex: "username" },
    { title: "Email", dataIndex: "email" },
    { title: "Role", dataIndex: "user_type" },
    { title: "Gender", dataIndex: "gender", render: (g) => g || "-" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button type="primary" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button danger onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16, background: "#f4f6f8", minHeight: "100vh" }}>
      <Title level={3}>User Management</Title>
      <Text type="secondary">Administration Panel</Text>

      {/* ================= FORM ================= */}
      <Card style={{ marginTop: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          {/* Avatar */}
          <Col xs={24} md={6} style={{ textAlign: "center" }}>
            <Avatar
              size={100}
              src={preview || defaultAvatar}
              style={{ marginBottom: 10 }}
            />

            <Upload
              beforeUpload={() => false}
              onChange={(info) => {
                const file = info.file;
                setPreview(URL.createObjectURL(file));
                form.setFieldsValue({
                  profile_image: info,
                });
              }}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
          </Col>

          {/* Form */}
          <Col xs={24} md={18}>
            <Form
              layout="vertical"
              form={form}
              onFinish={handleSubmit}
              initialValues={emptyForm}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="username" label="Username" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item name="email" label="Email" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              {!editingUser && (
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="password" label="Password">
                      <Input.Password />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item name="confirm_password" label="Confirm Password">
                      <Input.Password />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="user_type" label="Role">
                    <Select>
                      <Select.Option value="owner">Owner</Select.Option>
                      <Select.Option value="admin">Admin</Select.Option>
                      <Select.Option value="sales_agent">Sales Agent</Select.Option>
                      <Select.Option value="card_holder">Card Holder</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item name="gender" label="Gender">
                    <Select allowClear>
                      <Select.Option value="Male">Male</Select.Option>
                      <Select.Option value="Female">Female</Select.Option>
                      <Select.Option value="Other">Other</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Button type="primary" htmlType="submit" loading={loading} block>
                {editingUser ? "Update User" : "Create User"}
              </Button>
            </Form>
          </Col>
        </Row>
      </Card>

      {/* ================= FILTER ================= */}
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col xs={24} md={12}>
          <Input
            placeholder="Search user..."
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>

        <Col xs={24} md={12}>
          <Select
            placeholder="Filter by role"
            style={{ width: "100%" }}
            allowClear
            onChange={(v) => setRoleFilter(v)}
          >
            <Select.Option value="owner">Owner</Select.Option>
            <Select.Option value="admin">Admin</Select.Option>
            <Select.Option value="sales_agent">Sales Agent</Select.Option>
            <Select.Option value="card_holder">Card Holder</Select.Option>
          </Select>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}
      <Card style={{ marginTop: 16 }}>
        <Table
          dataSource={filteredUsers}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 8 }}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  );
}

